import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { GitBranch, Plus, Pencil, Trash2, X, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { Button, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { message, Input } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import '../../pages/technical/HeatProfilePage.css';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import axiosConfig from '../../services/axiosConfig';
import {
  fetchSowDetails,
  addSowDetails,
  updateSowDetails,
  deleteSowDetails,
} from '../../redux/sowDetails/actions';
import { NDT_PIPELINE_TYPE_OPTIONS } from '../../constants/ndtPipelineTypeOptions';
import ModernMultiCheckboxSelect from '../common/ModernMultiCheckboxSelect';
import {
  ndtTypesFromStationEntry,
  ndtStationToPayload,
  projectNdtTypesArray,
  formatProjectNdtTypesLabel,
} from '../../utils/sowNdtStation';
import {
  sumProjectExpectedLength,
  formatExpectedLength,
} from '../../utils/sowExpectedLength';
import {
  YES_NO_OPTIONS,
  parseWallThicknessFromRecord,
  formatWallThicknessDisplay,
  buildWallThicknessApiFields,
  parseExpectedJointsFromRecord,
  buildJointsPlannedWorkFields,
  parseSowPipelineExtrasFromRecord,
  buildSowPipelineExtras,
  readSowSectionId,
  sumExpectedJointsAcrossRecords,
  formatExpectedJointsChipParts,
} from '../../utils/sowPipelineForm';
import { isOffshoreProject } from '../../utils/projectConfig';
import './DPRSettingsPipelinesTab.css';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';

/** Build production-line rows from API SOW record */
function productionLineRowsFromSow(record, projectType) {
  if (!record) return [];
  const pt = String(projectType || '').trim().toLowerCase();
  const ndtRaw = record.ndtStations || record.ndt_stations || [];
  const ndtByNum = {};
  ndtRaw.forEach((entry) => {
    const n = entry.stationNumber ?? entry.station_number;
    if (n != null && n !== '') ndtByNum[Number(n)] = ndtTypesFromStationEntry(entry);
  });

  const weld = record.weldingStations || record.welding_stations;
  const coat = record.coatingStations || record.coating_stations;
  let nums = [];
  if (pt === 'welding') {
    nums = Array.isArray(weld) ? [...weld] : [];
  } else if (pt === 'coating') {
    nums = Array.isArray(coat) ? [...coat] : [];
  } else {
    nums = [
      ...new Set([
        ...(Array.isArray(weld) ? weld : []),
        ...(Array.isArray(coat) ? coat : []),
      ]),
    ];
  }
  nums = nums
    .map((x) => Number(x))
    .filter((n) => !Number.isNaN(n) && n > 0);

  // Union with every station listed in ndtStations (API can list NDT-only stations not in coating/welding arrays)
  const numsFromNdt = ndtRaw
    .map((e) => Number(e.stationNumber ?? e.station_number))
    .filter((n) => !Number.isNaN(n) && n > 0);
  nums = [...new Set([...nums, ...numsFromNdt])].sort((a, b) => a - b);

  const rows = nums.map((n, idx) => ({
    id: `pl-${record.id}-${n}-${idx}`,
    stationNumber: String(n),
    ndtTypes: ndtByNum[n] || [],
  }));
  // One extra blank row at the end so users can add another station without clicking Add row
  rows.push({
    id: `pl-${record.id}-blank-${Date.now()}`,
    stationNumber: '',
    ndtTypes: [],
  });
  return rows;
}

/** When production-line UI is hidden (coating), preserve API payload on pipeline edit. */
function copyProductionLineFromSowRecord(record, projectType) {
  if (!record || typeof record !== 'object') return {};
  const pt = String(projectType || '').trim().toLowerCase();
  const out = {};
  const sortedUniqueInts = (arr) =>
    [
      ...new Set(
        (Array.isArray(arr) ? arr : [])
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n) && n > 0)
      ),
    ].sort((a, b) => a - b);

  if (pt === 'welding') {
    const w = sortedUniqueInts(record.weldingStations || record.welding_stations);
    if (w.length) out.weldingStations = w;
  } else if (pt === 'coating') {
    const c = sortedUniqueInts(record.coatingStations || record.coating_stations);
    if (c.length) out.coatingStations = c;
  } else if (pt === 'both') {
    const w = sortedUniqueInts(record.weldingStations || record.welding_stations);
    const c = sortedUniqueInts(record.coatingStations || record.coating_stations);
    if (w.length) out.weldingStations = w;
    if (c.length) out.coatingStations = c;
  }

  const ndtRaw = record.ndtStations || record.ndt_stations;
  if (Array.isArray(ndtRaw) && ndtRaw.length > 0) {
    const ndtStations = ndtRaw
      .map((entry) => {
        const stationNumber = Number(
          typeof entry === 'object' && entry != null
            ? entry.stationNumber ?? entry.station_number
            : entry
        );
        return ndtStationToPayload(stationNumber, ndtTypesFromStationEntry(entry));
      })
      .filter((x) => !Number.isNaN(x.stationNumber) && x.stationNumber > 0);
    if (ndtStations.length) out.ndtStations = ndtStations;
  }
  return out;
}

const formatSowOptionalNumber = (v) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (Number.isNaN(n)) return '—';
  return String(n);
};

const toNullableNumber = (s) => {
  const t = (s ?? '').toString().trim();
  if (t === '') return null;
  const n = parseFloat(t);
  return Number.isNaN(n) ? null : n;
};

const readSowNumber = (record, camel, snake) =>
  (record == null ? undefined : record[camel] ?? record[snake]);

const PIPELINE_FACET_KEYS_BASE = [
  'pipelineSection',
  'sectionId',
  'material',
  'fieldJointType',
  'blastStandard',
  'rootWeldingTechnique',
  '__odDisplay',
  '__wtDisplay',
  '__eplDisplay',
  '__expectedJointsStr',
];

const ONSHORE_PIPELINE_FACET_KEYS = ['pipeStringId', 'stalkId'];

/** Offshore hides stalk/pipe string only; all other redesigned pipeline columns stay visible. */
const buildPipelineFacetKeys = (includeOnshoreFields) =>
  includeOnshoreFields
    ? [
        'pipelineSection',
        'sectionId',
        ...ONSHORE_PIPELINE_FACET_KEYS,
        'material',
        'fieldJointType',
        'blastStandard',
        'rootWeldingTechnique',
        '__odDisplay',
        '__wtDisplay',
        '__eplDisplay',
        '__expectedJointsStr',
      ]
    : PIPELINE_FACET_KEYS_BASE;

const buildPipelineColumnFiltersEmpty = (facetKeys) =>
  facetKeys.reduce((acc, k) => {
    acc[k] = '';
    return acc;
  }, {});

function enrichPipelineRowForFacets(row) {
  if (!row || typeof row !== 'object') return row;
  const extras = parseSowPipelineExtrasFromRecord(row);
  return {
    ...row,
    sectionId: readSowSectionId(row),
    rootWeldingTechnique: extras.rootWeldingTechnique,
    __odDisplay: formatSowOptionalNumber(readSowNumber(row, 'outerDiameterInch', 'outer_diameter_inch')),
    __wtDisplay: formatWallThicknessDisplay(row),
    __eplDisplay: formatSowOptionalNumber(readSowNumber(row, 'expectedPipeLength', 'expected_pipe_length')),
    __expectedJointsStr: parseExpectedJointsFromRecord(row) || '—',
  };
}

const SimpleSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
  open,
  onOpenChange,
  containerRef,
}) => {
  const selected = options.find((o) => o.value === value);
  const displayLabel = selected ? selected.label : '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef.current.contains(e.target))
        onOpenChange?.(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [containerRef, onOpenChange]);

  return (
    <div ref={containerRef} className="dpr-settings-pipelines-select-wrap">
      <div
        className="dpr-settings-pipelines-select-trigger"
        onClick={() => !disabled && onOpenChange?.(!open)}
        style={{
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span className="dpr-settings-pipelines-select-value">
          {displayLabel || placeholder}
        </span>
        {open ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>
      {open && (
        <div className="dpr-settings-pipelines-select-dropdown">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="dpr-settings-pipelines-select-opt"
              onClick={() => {
                onChange(opt.value);
                onOpenChange(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RowSelect = ({ value, options, onChange, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  return (
    <SimpleSelect
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      open={open}
      onOpenChange={setOpen}
      containerRef={ref}
      disabled={disabled}
    />
  );
};

const DPRSettingsPipelinesTab = ({
  selectedProject,
  sowData,
  isSowLoading,
  projectTeamMembersAccessList,
  userAccessList,
  onFetchSowDetails,
  onAddSowDetails,
  onUpdateSowDetails,
  onDeleteSowDetails,
  onAddPqtActivities,
  addPqtActivitiesDisabled = false,
}) => {
  const projectNdtConfigured = projectNdtTypesArray(selectedProject).map((t) => String(t).trim().toUpperCase());
  const allowedNdtOptions = useMemo(() => {
    if (!projectNdtConfigured.length || projectNdtConfigured.includes('ALL')) return NDT_PIPELINE_TYPE_OPTIONS;
    const allowed = new Set(projectNdtConfigured);
    return NDT_PIPELINE_TYPE_OPTIONS.filter((opt) => allowed.has(String(opt.value).toUpperCase()));
  }, [projectNdtConfigured.join('|')]);
  const [heatColumnFilters, setHeatColumnFilters] = useState(() =>
    buildPipelineColumnFiltersEmpty(PIPELINE_FACET_KEYS_BASE)
  );
  const [pipelineSort, setPipelineSort] = useState({
    key: 'pipelineSection',
    desc: false,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [copySourceRecord, setCopySourceRecord] = useState(null);
  const [copyPipeGroup, setCopyPipeGroup] = useState('');
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [formPipelineSection, setFormPipelineSection] = useState('');
  const [formSectionId, setFormSectionId] = useState('');
  const [formStalkId, setFormStalkId] = useState('');
  const [formPipeStringId, setFormPipeStringId] = useState('');
  const [formMaterial, setFormMaterial] = useState('');
  const [formFieldJointType, setFormFieldJointType] = useState('');
  const [formBlastStandard, setFormBlastStandard] = useState('');
  const [formRootWeldingTechnique, setFormRootWeldingTechnique] = useState('');
  const [formHasInternalLiner, setFormHasInternalLiner] = useState(undefined);
  const [formInternalLinerType, setFormInternalLinerType] = useState('');
  const [internalLinerTypeError, setInternalLinerTypeError] = useState(false);
  const [formHasInternalCoating, setFormHasInternalCoating] = useState(undefined);
  const [formInternalCoatingType, setFormInternalCoatingType] = useState('');
  const [internalCoatingTypeError, setInternalCoatingTypeError] = useState(false);
  const [formExpectedJoints, setFormExpectedJoints] = useState('');
  const [formOuterDiameterInch, setFormOuterDiameterInch] = useState('');
  const [formWallThicknessValues, setFormWallThicknessValues] = useState(['']);
  const [formExpectedPipeLength, setFormExpectedPipeLength] = useState('');
  const [formActualPipelineLength, setFormActualPipelineLength] = useState('');
  const [formActualWelds, setFormActualWelds] = useState('');

  // Production Line Set-Up (per pipeline) — table-row model
  const [plStationRows, setPlStationRows] = useState([]);
  const rawProjectType = selectedProject?.projectType ?? selectedProject?.project_type ?? '';
  const projectType = String(rawProjectType).trim();
  const projectTypeLower = projectType.toLowerCase();
  // Stalk # and Pipe String ID are onshore-only; offshore keeps the full redesigned form otherwise
  const showOnshorePipelineFields = !isOffshoreProject(selectedProject);
  const pipelineFacetKeys = useMemo(
    () => buildPipelineFacetKeys(showOnshorePipelineFields),
    [showOnshorePipelineFields]
  );
  // Default to showing unless explicitly Coating
  const showProductionLineSetup = projectTypeLower !== 'coating';
  const projectNdtPrefill = projectNdtTypesArray(selectedProject).filter((t) => t !== 'Both');

  const addPlRow = () => {
    setPlStationRows((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        stationNumber: '',
        ndtTypes: projectNdtPrefill.length ? [...projectNdtPrefill] : [],
      },
    ]);
  };

  const removePlRow = (id) => setPlStationRows((prev) => prev.filter((r) => r.id !== id));

  const updatePlRow = (id, key, value) =>
    setPlStationRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const addWallThicknessRow = () =>
    setFormWallThicknessValues((prev) => [...prev, '']);

  const updateWallThicknessRow = (index, value) =>
    setFormWallThicknessValues((prev) =>
      prev.map((v, i) => (i === index ? value : v))
    );

  const removeWallThicknessRow = (index) =>
    setFormWallThicknessValues((prev) =>
      prev.length <= 1 ? [''] : prev.filter((_, i) => i !== index)
    );

  const resetProductionLine = () => setPlStationRows([]);

  const setProductionLineOneBlankRow = () => {
    setPlStationRows([
      {
        id: Date.now() + Math.random(),
        stationNumber: '',
        ndtTypes: projectNdtPrefill.length ? [...projectNdtPrefill] : [],
      },
    ]);
  };

  const pipelineData = Array.isArray(sowData) ? sowData : [];

  const pipelineHeaderMetrics = useMemo(() => {
    const totalExpectedLengthM = sumProjectExpectedLength(pipelineData);
    const totalExpectedJoints = sumExpectedJointsAcrossRecords(pipelineData);
    return { totalExpectedLengthM, totalExpectedJoints };
  }, [pipelineData]);

  const facetSourceRows = useMemo(() => {
    if (!selectedProject?.id) return [];
    return pipelineData.map((row) => enrichPipelineRowForFacets(row));
  }, [pipelineData, selectedProject?.id]);

  const handleHeatColumnFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const pipelineFacetRef = useRef([]);
  pipelineFacetRef.current = facetSourceRows;
  const fetchPipelineFacetRows = useCallback(async () => {
    const list = pipelineFacetRef.current;
    return Array.isArray(list) ? list : [];
  }, []);

  const pipelineFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: true,
    filterableColumns: pipelineFacetKeys,
    listCount: facetSourceRows.length,
    projectList: facetSourceRows,
    searchCriteria: pipelineFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleHeatColumnFilterChange,
    fetchFacetRows: fetchPipelineFacetRows,
  });

  const lc = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredAndFaceted = useMemo(() => {
    let list = [...facetSourceRows];
    pipelineFacetKeys.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lc(raw);
      list = list.filter((row) => lc(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(list);
  }, [facetSourceRows, heatColumnFilters, applyFacetClientFilter, pipelineFacetKeys]);

  const handleSortClick = useCallback((field) => {
    setPipelineSort((prev) =>
      prev.key === field ? { ...prev, desc: !prev.desc } : { key: field, desc: false }
    );
  }, []);

  const renderSortCaret = useCallback(
    (field) =>
      pipelineSort.key === field ? (
        pipelineSort.desc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [pipelineSort.key, pipelineSort.desc]
  );

  const renderPipelineHeader = useCallback(
    (labelNode, dataIndex, sortField, { filterable = true, headerAlign = 'center', searchWidth = 120 } = {}) => {
      if (!filterable) {
        return (
          <div className="column-header-with-filter column-header-with-filter--no-search">
            <div
              className={`column-header-text-wrapper dpr-pipelines-heat-title-row${
                headerAlign === 'left' ? ' dpr-pipelines-heat-title-row--left' : ''
              }`}
            >
              <span className="dpr-pipelines-heat-label">{labelNode}</span>
            </div>
          </div>
        );
      }
      return (
        <div className="column-header-with-filter">
          <div
            className={`column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row${
              headerAlign === 'left' ? ' dpr-pipelines-heat-title-row--left' : ''
            }`}
          >
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSortClick(sortField);
                }
              }}
            >
              {labelNode}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSortClick(sortField);
                }
              }}
            >
              {renderSortCaret(sortField)}
            </span>
            {renderTextColumnFilterTrigger(dataIndex)}
          </div>
          <div className="header-filter-input user-mgmt-header-search">
            <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
              <Input
                placeholder={
                  dataIndex === 'pipelineSection'
                    ? 'Search By Pipeline Section'
                    : 'Search'
                }
                value={heatColumnFilters[dataIndex] ?? ''}
                onChange={(e) => handleHeatColumnFilterChange(dataIndex, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                size="small"
                style={{ width: searchWidth }}
              />
            </div>
          </div>
        </div>
      );
    },
    [
      handleHeatColumnFilterChange,
      handleSortClick,
      heatColumnFilters,
      renderSortCaret,
      renderTextColumnFilterTrigger,
    ]
  );

  const sortedDisplayList = useMemo(() => {
    const list = [...filteredAndFaceted];
    /** Natural / numeric-aware collation for pipeline IDs, sections, and similar columns */
    const cmpStr = (a, b, key) =>
      String(get(a, key) ?? '')
        .trim()
        .toLowerCase()
        .localeCompare(String(get(b, key) ?? '').trim().toLowerCase(), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
    const cmpNum = (a, b, camel, snake) => {
      const rawA = readSowNumber(a, camel, snake);
      const rawB = readSowNumber(b, camel, snake);
      const na = Number(rawA);
      const nb = Number(rawB);
      const aOk = rawA != null && rawA !== '' && Number.isFinite(na);
      const bOk = rawB != null && rawB !== '' && Number.isFinite(nb);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      if (na === nb) return 0;
      return na < nb ? -1 : 1;
    };
    const tieBreak = (a, b) => {
      const idA = a?.id != null ? Number(a.id) : NaN;
      const idB = b?.id != null ? Number(b.id) : NaN;
      if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) {
        return idA < idB ? -1 : 1;
      }
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
    };
    const { key: sortKey, desc: sortDesc } = pipelineSort;
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'pipelineSection':
        case 'sectionId':
        case 'pipeStringId':
        case 'stalkId':
        case 'material':
        case 'fieldJointType':
        case 'blastStandard':
        case 'rootWeldingTechnique':
        case '__expectedJointsStr':
          cmp = cmpStr(a, b, sortKey);
          break;
        case '__odDisplay':
          cmp = cmpNum(a, b, 'outerDiameterInch', 'outer_diameter_inch');
          break;
        case '__wtDisplay':
          cmp = cmpNum(a, b, 'wallThicknessMm', 'wall_thickness_mm');
          break;
        case '__eplDisplay':
          cmp = cmpNum(a, b, 'expectedPipeLength', 'expected_pipe_length');
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = tieBreak(a, b);
      return sortDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredAndFaceted, pipelineSort]);

  const isWriteAccess = (() => {
    const groups =
      userAccessList?.length > 0
        ? userAccessList
        : (() => {
          try {
            return JSON.parse(localStorage.getItem('UserGroup') || '[]');
          } catch (e) {
            return [];
          }
        })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Details?.write
    )
      return false;
    return true;
  })();

  useEffect(() => {
    setHeatColumnFilters(buildPipelineColumnFiltersEmpty(pipelineFacetKeys));
    setPipelineSort({ key: 'pipelineSection', desc: false });
    if (selectedProject?.id) {
      onFetchSowDetails(selectedProject.id);
    }
  }, [selectedProject?.id, onFetchSowDetails, pipelineFacetKeys]);

  const resetForm = () => {
    setFormPipelineSection('');
    setFormSectionId('');
    setFormStalkId('');
    setFormPipeStringId('');
    setFormMaterial('');
    setFormFieldJointType('');
    setFormBlastStandard('');
    setFormRootWeldingTechnique('');
    setFormHasInternalLiner(undefined);
    setFormInternalLinerType('');
    setInternalLinerTypeError(false);
    setFormHasInternalCoating(undefined);
    setFormInternalCoatingType('');
    setInternalCoatingTypeError(false);
    setFormExpectedJoints('');
    setFormOuterDiameterInch('');
    setFormWallThicknessValues(['']);
    setFormExpectedPipeLength('');
    setFormActualPipelineLength('');
    setFormActualWelds('');
    setEditRecord(null);
    resetProductionLine();
  };

  const openAddModal = () => {
    if (!selectedProject?.id) {
      message.warning('Select a project to add pipeline.');
      return;
    }
    resetForm();
    if (showProductionLineSetup) {
      setProductionLineOneBlankRow();
    } else {
      resetProductionLine();
    }
    setShowAddModal(true);
  };

  const openEditModal = (record) => {
    const extras = parseSowPipelineExtrasFromRecord(record);
    setEditRecord(record);
    setFormPipelineSection(record?.pipelineSection ?? '');
    setFormSectionId(extras.sectionId);
    setFormStalkId(record?.stalkId ?? '');
    setFormPipeStringId(record?.pipeStringId ?? '');
    setFormMaterial(record?.material ?? '');
    setFormFieldJointType(record?.fieldJointType ?? record?.field_joint_type ?? '');
    setFormBlastStandard(record?.blastStandard ?? record?.blast_standard ?? '');
    setFormRootWeldingTechnique(extras.rootWeldingTechnique);
    setFormHasInternalLiner(extras.hasInternalLiner);
    setFormInternalLinerType(extras.internalLinerType);
    setInternalLinerTypeError(false);
    setFormHasInternalCoating(extras.hasInternalCoating);
    setFormInternalCoatingType(extras.internalCoatingType);
    setInternalCoatingTypeError(false);
    setFormExpectedJoints(parseExpectedJointsFromRecord(record));
    setFormOuterDiameterInch(
      (() => {
        const v = readSowNumber(record, 'outerDiameterInch', 'outer_diameter_inch');
        return v != null && v !== '' ? String(v) : '';
      })()
    );
    setFormWallThicknessValues(parseWallThicknessFromRecord(record));
    setFormExpectedPipeLength(
      (() => {
        const v = readSowNumber(record, 'expectedPipeLength', 'expected_pipe_length');
        return v != null && v !== '' ? String(v) : '';
      })()
    );
    setFormActualPipelineLength(
      (() => {
        const v = readSowNumber(record, 'actualPipelineLength', 'actual_pipeline_length');
        return v != null && v !== '' ? String(v) : '';
      })()
    );
    setFormActualWelds(
      (() => {
        const v = readSowNumber(record, 'actualWelds', 'actual_welds');
        return v != null && v !== '' ? String(v) : '';
      })()
    );
    setPlStationRows(
      showProductionLineSetup ? productionLineRowsFromSow(record, projectType) : []
    );
    setShowAddModal(true);
  };

  const openCopyModal = (record) => {
    if (!selectedProject?.id || !record?.id) {
      message.warning('Select a project and pipeline to copy.');
      return;
    }
    setCopySourceRecord(record);
    const baseName = record.pipelineSection || readSowSectionId(record) || `Mainline ${record.id}`;
    setCopyPipeGroup(`${baseName} (copy)`);
    setIsCopyModalOpen(true);
  };

  const closeCopyModal = () => {
    setIsCopyModalOpen(false);
    setIsCopying(false);
    setCopySourceRecord(null);
    setCopyPipeGroup('');
  };

  const handleConfirmCopy = async () => {
    if (isCopying) return;
    if (!selectedProject?.id || !copySourceRecord?.id) return;
    const pipeGroup = String(copyPipeGroup || '').trim();
    if (!pipeGroup) {
      message.warning('Enter a name for the copied mainline.');
      return;
    }
    setIsCopying(true);
    try {
      await axiosConfig.post('/production-joint/copy-mainline', {
        projectId: selectedProject.id,
        sourceMainlineId: copySourceRecord.id,
        pipeGroup,
      });
      message.success('Mainline copied successfully.');
      closeCopyModal();
      onFetchSowDetails(selectedProject.id);
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'Failed to copy mainline.');
      setIsCopying(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const isPipeStringIdUnique = (pipeStringId, excludeId) => {
    const trimmed = (pipeStringId || '').trim();
    if (!trimmed) return false;
    return !pipelineData.some(
      (item) => item.pipeStringId === trimmed && item.id !== excludeId
    );
  };

  const handleSave = () => {
    const pipelineSection = (formPipelineSection || '').trim();
    const sectionId = (formSectionId || '').trim();
    const material = (formMaterial || '').trim();
    const stalkId = (formStalkId ?? '').toString().trim();
    const fieldJointType = (formFieldJointType || '').trim();
    const blastStandard = (formBlastStandard || '').trim();
    const pipeStringId = (formPipeStringId || '').trim();

    const missingOnshoreFields =
      showOnshorePipelineFields && (!stalkId || !pipeStringId);

    if (
      !pipelineSection ||
      !sectionId ||
      !material ||
      !fieldJointType ||
      !blastStandard ||
      missingOnshoreFields
    ) {
      message.warning('Fill all the required fields.');
      return;
    }

    if (showOnshorePipelineFields && !isPipeStringIdUnique(pipeStringId, editRecord?.id)) {
      message.warning('Pipe String ID is already in use. Try another.');
      return;
    }

    const linerTypeMissing =
      formHasInternalLiner === 'Yes' && !String(formInternalLinerType || '').trim();
    const coatingTypeMissing =
      formHasInternalCoating === 'Yes' && !String(formInternalCoatingType || '').trim();
    setInternalLinerTypeError(linerTypeMissing);
    setInternalCoatingTypeError(coatingTypeMissing);
    if (linerTypeMissing) {
      message.warning('Enter internal liner type when Internal Liner is Yes.');
      return;
    }
    if (coatingTypeMissing) {
      message.warning('Enter internal coating type when Internal Coating is Yes.');
      return;
    }

    let productionLine = {};
    if (showProductionLineSetup) {
      const validRows = plStationRows.filter(
        (r) => !isNaN(Number(r.stationNumber)) && Number(r.stationNumber) > 0
      );
      const uniqueRows = Object.values(
        validRows.reduce((acc, r) => {
          const n = Number(r.stationNumber);
          if (!acc[n]) {
            const types = Array.isArray(r.ndtTypes) ? r.ndtTypes.filter(Boolean) : [];
            acc[n] = {
              stationNumber: n,
              ndtTypes: types.length ? [...types] : [...projectNdtPrefill],
            };
          }
          return acc;
        }, {})
      ).sort((a, b) => a.stationNumber - b.stationNumber);
      const stationNums = uniqueRows.map((r) => r.stationNumber);

      if (stationNums.length > 0) {
        if (projectTypeLower === 'coating') {
          productionLine.coatingStations = stationNums;
        } else if (projectTypeLower === 'both') {
          productionLine.weldingStations = stationNums;
          productionLine.coatingStations = stationNums;
        } else {
          // Default to welding if empty or 'welding'
          productionLine.weldingStations = stationNums;
        }
        const ndtRows = uniqueRows.filter((r) => Array.isArray(r.ndtTypes) && r.ndtTypes.length > 0);
        if (ndtRows.length > 0) {
          productionLine.ndtStations = ndtRows.map(({ stationNumber, ndtTypes }) =>
            ndtStationToPayload(stationNumber, ndtTypes)
          );
        }
      }
    } else if (editRecord) {
      productionLine = copyProductionLineFromSowRecord(editRecord, projectType);
    }

    const payload = {
      pipelineSection,
      material,
      fieldJointType,
      blastStandard,
      ...(showOnshorePipelineFields ? { stalkId, pipeStringId } : {}),
      ...buildSowPipelineExtras({
        sectionId,
        rootWeldingTechnique: formRootWeldingTechnique,
        hasInternalLiner: formHasInternalLiner,
        internalLinerType: formInternalLinerType,
        hasInternalCoating: formHasInternalCoating,
        internalCoatingType: formInternalCoatingType,
      }),
      ...buildJointsPlannedWorkFields(formExpectedJoints),
      outerDiameterInch: toNullableNumber(formOuterDiameterInch),
      ...buildWallThicknessApiFields(formWallThicknessValues),
      expectedPipeLength: toNullableNumber(formExpectedPipeLength),
      actualPipelineLength: toNullableNumber(formActualPipelineLength),
      actualWelds: toNullableNumber(formActualWelds),
      ...productionLine,
    };

    if (editRecord) {
      onUpdateSowDetails({
        data: {
          id: editRecord.id,
          projectId: editRecord.projectId ?? selectedProject.id,
          ...payload
        }
      });
      closeAddModal();
    } else {
      onAddSowDetails({
        projectId: selectedProject.id,
        ...payload
      });
      closeAddModal();
    }
  };

  const showDeleteModal = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!recordToDelete) return;
    onDeleteSowDetails({ data: recordToDelete });
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
  };

  if (!selectedProject) {
    return (
      <div className="dpr-settings-pipelines-tab">
        <div className="dpr-settings-pipelines-card">
          <p style={{ margin: 0, color: '#64748B' }}>
            Select a project to manage pipelines.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dpr-settings-pipelines-tab">
      <div className="dpr-settings-pipelines-layout">
        <div className="dpr-settings-pipelines-main">
          <div className="dpr-settings-pipelines-card">
            <div className="dpr-settings-pipelines-header">
              <div className="dpr-settings-pipelines-header-left">
                <div className="dpr-settings-pipelines-icon-wrap">
                  <GitBranch size={24} />
                </div>
                <div className="dpr-settings-pipelines-header-content">
                  <div className="dpr-settings-pipelines-title-row">
                    <h3 className="dpr-settings-pipelines-title">Pipelines</h3>
                    <span className="dpr-settings-pipelines-summary">
                      {sortedDisplayList.length} pipeline{sortedDisplayList.length !== 1 ? 's' : ''}
                    </span>
                    <div className="dpr-settings-pipelines-metrics" role="list">
                      {pipelineHeaderMetrics.totalExpectedLengthM != null && (
                        <span className="dpr-settings-pipelines-metric-chip" role="listitem">
                          <span className="dpr-settings-pipelines-metric-label">TOTAL EXPECTED PIPELINE LENGTH</span>
                          <span className="dpr-settings-pipelines-metric-value">
                            {formatExpectedLength(pipelineHeaderMetrics.totalExpectedLengthM)} m
                          </span>
                        </span>
                      )}
                      {pipelineHeaderMetrics.totalExpectedJoints != null && (() => {
                        const { label, value } = formatExpectedJointsChipParts(
                          pipelineHeaderMetrics.totalExpectedJoints
                        );
                        return (
                          <span
                            className="dpr-settings-pipelines-metric-chip dpr-settings-pipelines-metric-chip--planned"
                            role="listitem"
                          >
                            <span className="dpr-settings-pipelines-metric-label">{label}</span>
                            <span className="dpr-settings-pipelines-metric-value">{value}</span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <p className="dpr-settings-pipelines-description">
                    Manage pipeline sections (Statement of Work): add, edit, delete.
                  </p>
                </div>
              </div>
              <div className="dpr-settings-pipelines-actions">
                <Button
                  label="Add New"
                  variant="primary"
                  size="small"
                  icon={Plus}
                  onClick={openAddModal}
                  disabled={!isWriteAccess}
                />
                {typeof onAddPqtActivities === 'function' && (
                  <Button
                    label="Add PQT Activities"
                    variant="primary"
                    size="small"
                    icon={Plus}
                    onClick={onAddPqtActivities}
                    disabled={addPqtActivitiesDisabled}
                  />
                )}
              </div>
            </div>

            <div className="dpr-settings-pipelines-table-wrap">
              <div className="dpr-settings-pipelines-thead">
                <div className="dpr-settings-pipelines-header-row dpr-settings-pipelines-header-row--heat">
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-num dpr-settings-pipelines-cell-header-compact">
                    {renderPipelineHeader('#', null, null, { filterable: false, headerAlign: 'center' })}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-section">
                    {renderPipelineHeader('PIPELINE SECTION', 'pipelineSection', 'pipelineSection', {
                      headerAlign: 'left',
                    })}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">
                    {renderPipelineHeader('ID #', 'sectionId', 'sectionId', {
                      headerAlign: 'left',
                    })}
                  </div>
                  {showOnshorePipelineFields && (
                    <>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">
                        {renderPipelineHeader('PIPE STRING ID', 'pipeStringId', 'pipeStringId', {
                          headerAlign: 'left',
                        })}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {renderPipelineHeader('STALK #', 'stalkId', 'stalkId')}
                      </div>
                    </>
                  )}
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                    {renderPipelineHeader('PIPE GRADE', 'material', 'material')}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                    {renderPipelineHeader('COATING TYPE', 'fieldJointType', 'fieldJointType')}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                    {renderPipelineHeader('BLAST STANDARD', 'blastStandard', 'blastStandard')}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                    {renderPipelineHeader('ROOT WELDING TECHNIQUE', 'rootWeldingTechnique', 'rootWeldingTechnique')}
                  </div>
                  <div
                    className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim dpr-settings-pipelines-cell-narrow-search"
                    title="Outer Diameter (inch)"
                  >
                    {renderPipelineHeader('OD (inch)', '__odDisplay', '__odDisplay', {
                      searchWidth: 68,
                    })}
                  </div>
                  <div
                    className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim dpr-settings-pipelines-cell-narrow-search"
                    title="Wall thickness (mm)"
                  >
                    {renderPipelineHeader('WT (mm)', '__wtDisplay', '__wtDisplay', {
                      searchWidth: 68,
                    })}
                  </div>
                  <div
                    className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim dpr-settings-pipelines-cell-narrow-search"
                    title="Expected pipeline length"
                  >
                    {renderPipelineHeader('Expected pipeline length', '__eplDisplay', '__eplDisplay', {
                      searchWidth: 68,
                    })}
                  </div>
                  <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid dpr-settings-pipelines-cell-narrow-search">
                    {renderPipelineHeader('EXPECTED JOINTS', '__expectedJointsStr', '__expectedJointsStr', {
                      searchWidth: 68,
                    })}
                  </div>
                  <div
                    className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-actions dpr-settings-pipelines-cell-header-compact"
                    title="Actions"
                  >
                    {renderPipelineHeader('ACTIONS', null, null, { filterable: false, headerAlign: 'center' })}
                  </div>
                </div>
              </div>

              {isSowLoading ? (
                <div className="dpr-settings-pipelines-loading">
                  <Spinner size="medium" />
                </div>
              ) : sortedDisplayList.length === 0 ? (
                <div className="dpr-settings-pipelines-empty">
                  <ProjectTabTableEmpty />
                </div>
              ) : (
                <div className="dpr-settings-pipelines-rows">
                  {sortedDisplayList.map((row, index) => (
                    <div key={row.id} className="dpr-settings-pipelines-row">
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-num">
                        {index + 1}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-section">
                        {row.pipelineSection ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">
                        {readSowSectionId(row) || '—'}
                      </div>
                      {showOnshorePipelineFields && (
                        <>
                          <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-id">
                            {row.pipeStringId ?? '—'}
                          </div>
                          <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                            {row.stalkId ?? '—'}
                          </div>
                        </>
                      )}
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.material ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.fieldJointType ?? row.field_joint_type ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {row.blastStandard ?? row.blast_standard ?? '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {parseSowPipelineExtrasFromRecord(row).rootWeldingTechnique || '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim">
                        {formatSowOptionalNumber(
                          readSowNumber(row, 'outerDiameterInch', 'outer_diameter_inch')
                        )}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim">
                        {formatWallThicknessDisplay(row)}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-dim">
                        {formatSowOptionalNumber(
                          readSowNumber(row, 'expectedPipeLength', 'expected_pipe_length')
                        )}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-mid">
                        {parseExpectedJointsFromRecord(row) || '—'}
                      </div>
                      <div className="dpr-settings-pipelines-cell dpr-settings-pipelines-cell-actions">
                        <button
                          type="button"
                          className="dpr-settings-pipelines-action-btn"
                          onClick={() => openEditModal(row)}
                          disabled={!isWriteAccess}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          type="button"
                          className="dpr-settings-pipelines-action-btn"
                          onClick={() => openCopyModal(row)}
                          disabled={!isWriteAccess}
                          title="Copy"
                          aria-label="Copy"
                        >
                          <Copy size={18} />
                        </button>
                        <button
                          type="button"
                          className="dpr-settings-pipelines-action-btn dpr-settings-pipelines-action-delete"
                          onClick={() => showDeleteModal(row)}
                          disabled={!isWriteAccess}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Copy Mainline Modal */}
      {isCopyModalOpen && (
        <div className="dpr-settings-pipelines-modal-backdrop" onClick={closeCopyModal}>
          <div
            className="dpr-settings-pipelines-modal"
            style={{ width: '640px', maxWidth: '92vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dpr-settings-pipelines-modal-head">
              <h3 className="dpr-settings-pipelines-modal-title">Copy Mainline</h3>
              <button
                type="button"
                className="dpr-settings-pipelines-modal-close"
                onClick={closeCopyModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-pipelines-modal-body">
              <div className="dpr-settings-pipelines-field" style={{ marginBottom: '14px' }}>
                <label>Source</label>
                <div
                  className="dpr-settings-pipelines-input"
                  style={{
                    padding: '10px 12px',
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: '10px',
                    color: '#0F172A',
                  }}
                >
                  {(copySourceRecord?.pipelineSection || readSowSectionId(copySourceRecord) || `Mainline ${copySourceRecord?.id}`) ?? '—'}
                </div>
              </div>

              <div className="dpr-settings-pipelines-field">
                <label>New mainline name</label>
                <input
                  type="text"
                  className="dpr-settings-pipelines-input"
                  placeholder="e.g. Mainline 1 (copy)"
                  value={copyPipeGroup}
                  onChange={(e) => setCopyPipeGroup(e.target.value)}
                />
              </div>
            </div>
            <div className="dpr-settings-pipelines-modal-foot">
              <Button label="Cancel" variant="tertiary" size="medium" onClick={closeCopyModal} />
              <Button
                label={isCopying ? 'Copying...' : 'Copy'}
                variant="primary"
                size="medium"
                onClick={handleConfirmCopy}
                disabled={isCopying || !String(copyPipeGroup || '').trim()}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="dpr-settings-pipelines-modal-backdrop">
          <div
            className="dpr-settings-pipelines-modal"
            style={{ width: '900px', maxWidth: '90vw' }}
          >
            <div className="dpr-settings-pipelines-modal-head">
              <h3 className="dpr-settings-pipelines-modal-title">
                {editRecord ? 'Edit Pipeline Section' : 'Add Pipeline Section'}
              </h3>
              <button
                type="button"
                className="dpr-settings-pipelines-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-pipelines-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                <div className="dpr-settings-pipelines-field">
                  <label>Pipeline Section Name <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Pipeline Section Name"
                    value={formPipelineSection}
                    onChange={(e) => setFormPipelineSection(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>ID # <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Schedule ID"
                    value={formSectionId}
                    onChange={(e) => setFormSectionId(e.target.value)}
                  />
                </div>
                {showOnshorePipelineFields && (
                  <>
                    <div className="dpr-settings-pipelines-field">
                      <label>Pipe String ID <span className="required">*</span></label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Pipe String ID"
                        value={formPipeStringId}
                        onChange={(e) => setFormPipeStringId(e.target.value)}
                      />
                    </div>
                    <div className="dpr-settings-pipelines-field">
                      <label>Stalk # <span className="required">*</span></label>
                      <input
                        type="text"
                        className="dpr-settings-pipelines-input"
                        placeholder="Stalk #"
                        value={formStalkId}
                        onChange={(e) => setFormStalkId(e.target.value)}
                      />
                    </div>
                  </>
                )}
                <div className="dpr-settings-pipelines-field">
                  <label>Expected Joints</label>
                  <input
                    type="number"
                    min={0}
                    className="dpr-settings-pipelines-input"
                    placeholder="e.g. 450"
                    value={formExpectedJoints}
                    onChange={(e) => setFormExpectedJoints(e.target.value)}
                  />
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748B' }}>
                    Work type is fixed to Field Joints for welding and coating scope.
                  </p>
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Expected pipeline length</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="dpr-settings-pipelines-input"
                    placeholder="e.g. 1500"
                    value={formExpectedPipeLength}
                    onChange={(e) => setFormExpectedPipeLength(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Outer Diameter (inch)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="dpr-settings-pipelines-input"
                    placeholder="e.g. 12.75"
                    value={formOuterDiameterInch}
                    onChange={(e) => setFormOuterDiameterInch(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Wall Thickness (mm)</label>
                  {formWallThicknessValues.map((wt, index) => (
                    <div
                      key={`wt-${index}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 36px',
                        gap: '8px',
                        alignItems: 'center',
                        marginBottom: index < formWallThicknessValues.length - 1 ? '8px' : 0,
                      }}
                    >
                      <input
                        type="text"
                        inputMode="decimal"
                        className="dpr-settings-pipelines-input"
                        placeholder="e.g. 12.7"
                        value={wt}
                        onChange={(e) => updateWallThicknessRow(index, e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeWallThicknessRow(index)}
                        className="dpr-settings-pipelines-pl-remove-btn"
                        aria-label="Remove wall thickness"
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addWallThicknessRow}
                    className="dpr-settings-pipelines-pl-add-btn"
                    style={{ marginTop: '8px' }}
                  >
                    <Plus size={14} strokeWidth={2} />
                    <span>Add wall thickness</span>
                  </button>
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Pipe Grade <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Pipe Grade"
                    value={formMaterial}
                    onChange={(e) => setFormMaterial(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Internal Liner</label>
                  <RowSelect
                    value={formHasInternalLiner}
                    options={YES_NO_OPTIONS}
                    onChange={(val) => {
                      setFormHasInternalLiner(val);
                      if (val !== 'Yes') setInternalLinerTypeError(false);
                    }}
                    placeholder="Yes / No"
                  />
                  {formHasInternalLiner === 'Yes' && (
                    <>
                      <label style={{ marginTop: '8px' }}>
                        Internal Liner Type <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`dpr-settings-pipelines-input${
                          internalLinerTypeError ? ' dpr-settings-pipelines-input--error' : ''
                        }`}
                        placeholder="Internal liner type"
                        value={formInternalLinerType}
                        onChange={(e) => {
                          setFormInternalLinerType(e.target.value);
                          if (internalLinerTypeError && e.target.value.trim()) {
                            setInternalLinerTypeError(false);
                          }
                        }}
                      />
                      {internalLinerTypeError && (
                        <div className="dpr-settings-pipelines-field-error">
                          Internal liner type is required.
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Coating Type <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Coating Type"
                    value={formFieldJointType}
                    onChange={(e) => setFormFieldJointType(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Internal Coating</label>
                  <RowSelect
                    value={formHasInternalCoating}
                    options={YES_NO_OPTIONS}
                    onChange={(val) => {
                      setFormHasInternalCoating(val);
                      if (val !== 'Yes') setInternalCoatingTypeError(false);
                    }}
                    placeholder="Yes / No"
                  />
                  {formHasInternalCoating === 'Yes' && (
                    <>
                      <label style={{ marginTop: '8px' }}>
                        Internal Coating Type <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className={`dpr-settings-pipelines-input${
                          internalCoatingTypeError ? ' dpr-settings-pipelines-input--error' : ''
                        }`}
                        placeholder="Internal coating type"
                        value={formInternalCoatingType}
                        onChange={(e) => {
                          setFormInternalCoatingType(e.target.value);
                          if (internalCoatingTypeError && e.target.value.trim()) {
                            setInternalCoatingTypeError(false);
                          }
                        }}
                      />
                      {internalCoatingTypeError && (
                        <div className="dpr-settings-pipelines-field-error">
                          Internal coating type is required.
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Root Welding Technique</label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Root Welding Technique"
                    value={formRootWeldingTechnique}
                    onChange={(e) => setFormRootWeldingTechnique(e.target.value)}
                  />
                </div>
                <div className="dpr-settings-pipelines-field">
                  <label>Blast Standard <span className="required">*</span></label>
                  <input
                    type="text"
                    className="dpr-settings-pipelines-input"
                    placeholder="Blast Standard"
                    value={formBlastStandard}
                    onChange={(e) => setFormBlastStandard(e.target.value)}
                  />
                </div>
              </div>
              {/* ── Production Line Set-Up (welding / both only) ── */}
              {showProductionLineSetup && (
                <div style={{ marginTop: '24px' }}>
                  <div className="dpr-settings-pipelines-section-divider">
                    <span className="dpr-settings-pipelines-section-label" style={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>PRODUCTION LINE SET-UP</span>
                  </div>

                  {formatProjectNdtTypesLabel(selectedProject) ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        background: 'rgba(239,49,76,0.07)',
                        border: '1px solid rgba(239,49,76,0.25)',
                        fontSize: '12px',
                        color: '#ef314c',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>Project NDT Type:</span>
                      <span>{formatProjectNdtTypesLabel(selectedProject)}</span>
                    </div>
                  ) : null}

                  {plStationRows.length > 0 && (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 100px) minmax(0, 1fr) 32px',
                        gap: '8px',
                        padding: '4px 2px 8px 2px',
                        marginBottom: '6px',
                      }}
                    >
                      {['St. No.', 'NDT Type', ''].map((h, idx) => (
                        <span
                          key={h || `pl-h-${idx}`}
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#64748B',
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  )}

                  {plStationRows.map((row) => (
                    <div
                      key={row.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 100px) minmax(0, 1fr) 32px',
                        gap: '8px',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <input
                        type="number"
                        min="1"
                        className="dpr-settings-pipelines-input"
                        placeholder="No."
                        value={row.stationNumber}
                        onChange={(e) => updatePlRow(row.id, 'stationNumber', e.target.value)}
                        style={{ width: '100%', minWidth: 0, padding: '8px' }}
                      />

                      <ModernMultiCheckboxSelect
                        placeholder="Select NDT Type"
                        value={Array.isArray(row.ndtTypes) ? row.ndtTypes : []}
                        onChange={(vals) =>
                          updatePlRow(row.id, 'ndtTypes', Array.isArray(vals) ? vals : [])
                        }
                        options={allowedNdtOptions}
                        disabled={!isWriteAccess}
                      />

                      <button
                        type="button"
                        onClick={() => removePlRow(row.id)}
                        className="dpr-settings-pipelines-pl-remove-btn"
                        aria-label="Remove station row"
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addPlRow}
                    className="dpr-settings-pipelines-pl-add-btn"
                  >
                    <Plus size={14} strokeWidth={2} />
                    <span>Add row</span>
                  </button>
                </div>
              )}

              <p className="dpr-settings-pipelines-modal-hint" style={{ marginTop: '16px' }}>
                *Weld number will generate as shown in example: WeldName-Weld-01
              </p>
            </div>
            <div className="dpr-settings-pipelines-modal-foot">
              <Button label="Cancel" variant="tertiary" size="medium" onClick={closeAddModal} />
              <Button
                label={editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={
                  !(formPipelineSection || '').trim() ||
                  !(formSectionId || '').trim() ||
                  !(formMaterial || '').trim() ||
                  (showOnshorePipelineFields && !(formStalkId ?? '').toString().trim()) ||
                  !(formFieldJointType || '').trim() ||
                  !(formBlastStandard || '').trim() ||
                  (showOnshorePipelineFields && !(formPipeStringId || '').trim())
                }
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Pipeline Section"
        message={
          recordToDelete ? (
            <>
              Deleting this pipeline section will delete all corresponding Stations and related information.
              Are you sure you want to delete{' '}
              <strong style={{ color: '#EF4444' }}>{recordToDelete.pipelineSection}</strong>?
            </>
          ) : (
            'Are you sure you want to delete this pipeline section?'
          )
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ sow, equip }) => ({
  sowData: get(sow, 'sowData', []),
  isSowLoading: get(sow, 'isSowLoading', false),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchSowDetails: fetchSowDetails,
  onAddSowDetails: addSowDetails,
  onUpdateSowDetails: updateSowDetails,
  onDeleteSowDetails: deleteSowDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsPipelinesTab);
